import React, { useState } from "react";
import { Button, DataTable, DataTableColumn, TooltipIcon } from "@sscs/ui";
import { AllowedTo, FetchMoreData, Upload } from "Components";
import { PERMISSION } from "Constants";
import { useAppSelector } from "Hooks";
import { selectCreatedVersions } from "Store/projects.slice";
import { GetVersionsResponse, Package, UploadOption, Version } from "Types";
import { isApproved } from "Utils";
import styles from "./releasesList.module.scss";
import VersionRow from "./VersionRow";

const COLUMNS: DataTableColumn[] = [
  { id: "info", label: "Info", width: "70px" },
  { id: "status", label: "Status", width: "90px" },
  { id: "approval", label: "Approval", width: "135px" },
  { id: "version", label: "Version", width: "100%", minWidth: "210px" },
  { id: "assessment", label: "SAFE assessment", width: "400px" },
  { id: "released", label: "Released", width: "110px" },
  { id: "usage", label: "Usage", width: "70px" },
  { id: "actions", label: "", align: "center", width: "55px" },
];

type ReleasesListProps = {
  data: GetVersionsResponse | undefined;
  packageInfo: Package;
  uploadOption: UploadOption;
  onDerive: (predecessor: Version) => void;
  onRefetch: () => void;
  onUpload: (option: UploadOption) => void;
  onImportLatest: (() => void) | null;
  onUploadOptionChange: (option: UploadOption) => void;
};

const ReleasesList = ({
  data,
  packageInfo,
  uploadOption,
  onDerive,
  onRefetch,
  onUpload,
  onImportLatest,
  onUploadOptionChange,
}: ReleasesListProps): React.JSX.Element => {
  const createdVersions = useAppSelector(selectCreatedVersions);
  const [focusedRowID, setFocusedRowID] = useState<string | null>(null);
  const versions = data?.versions ?? [];
  const versionsLimit = data?.max_versions_per_package ?? 0;
  const versionCount = <strong>{data?.match_count ?? 0}</strong>;
  const approvedVersionsCount = versions.filter((version) =>
    isApproved(version.approval_status),
  ).length;

  return (
    <DataTable
      title="Versions"
      titleDetails={
        versionsLimit
          ? [
              <React.Fragment key="versions-limit">
                <span>
                  {versionCount} out of {versionsLimit} versions
                </span>
                <TooltipIcon
                  title={`limited to ${versionsLimit} per package`}
                />
              </React.Fragment>,
            ]
          : [<span key="versions">{versionCount} versions</span>]
      }
      titleActions={
        onImportLatest ? (
          <AllowedTo permissions={[PERMISSION.CREATE_PACKAGE]}>
            <Button
              disabled={packageInfo.external_catalog?.automatic_sync}
              tooltipTitle={
                packageInfo.external_catalog?.automatic_sync
                  ? "New versions are synced automatically"
                  : undefined
              }
              onClick={onImportLatest}
            >
              Import latest
            </Button>
          </AllowedTo>
        ) : (
          <AllowedTo permissions={[PERMISSION.UPLOAD_VERSION]}>
            <div className={styles["upload-action"]}>
              <Upload.Button
                selectedOption={uploadOption}
                onOptionChange={onUploadOptionChange}
                onClick={onUpload}
              />
            </div>
          </AllowedTo>
        )
      }
      size="large"
      columns={COLUMNS}
      empty={{ subject: "versions" }}
    >
      {[
        ...(createdVersions.length > 0
          ? [
              <FetchMoreData
                key="fetch-more"
                id="show-other-users-files"
                itemName="version"
                itemsCount={createdVersions.length}
                onRefetch={onRefetch}
              />,
            ]
          : []),
        ...versions.map((version, index) => (
          <VersionRow
            rowIndex={index}
            key={version.version_id}
            version={version}
            predecessor={versions.find(
              (item) => item.version_id === version.predecessor_id,
            )}
            inCatalog={
              packageInfo.in_catalog &&
              packageInfo.catalog_version_id === version.version_id
            }
            attachedToExternalCatalog={packageInfo.external_catalog !== null}
            isOnlyApprovedVersion={
              isApproved(version.approval_status) && approvedVersionsCount === 1
            }
            focusedRowID={focusedRowID}
            onFocusChange={() => setFocusedRowID(version.version_id)}
            onDerive={() => onDerive(version)}
          />
        )),
      ]}
    </DataTable>
  );
};

export default ReleasesList;
